import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { normalizeVersion, normalizeFuel } from '../utils/normalization.js';

export function FilterPanel({
  t,
  showFilters,
  setShowFilters,
  sourceCountry,
  setSourceCountry,
  targetCountry,
  setTargetCountry,
  filterMake,
  setFilterMake,
  filterModel,
  setFilterModel,
  filterVersion,
  setFilterVersion,
  filterFuelType,
  setFilterFuelType,
  maxPrice,
  setMaxPrice,
  minMileage,
  setMinMileage,
  maxMileage,
  setMaxMileage,
  minYear,
  setMinYear,
  maxYear,
  setMaxYear,
  availableCountries,
  availableMakes,
  availableModels,
  availableVersions,
  availableFuelTypes,
  allListings,
  qualityLevels,
  setQualityLevels,
  yearTolerance,
  setYearTolerance,
  mileageTolerance,
  setMileageTolerance,
}) {
  // Compute green-fill gradient for range sliders
  const sliderStyle = (value, min, max) => {
    const pct = ((value - min) / (max - min)) * 100;
    return { background: `linear-gradient(to right, #10b981 0%, #10b981 ${pct}%, #334155 ${pct}%, #334155 100%)`, borderRadius: '2px', height: '6px' };
  };

  const toggleQuality = (level) => {
    setQualityLevels(prev =>
      prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]
    );
  };
  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 mb-4 overflow-hidden">
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <span className="text-sm font-medium text-slate-300">{t('filters')}</span>
        {showFilters ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {showFilters && (
        <div className="px-5 pb-4 border-t border-slate-700/50 pt-3">
          {/* Row 1: Source, Target, Make, Model */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('sourceMarket')}</label>
              <select
                value={sourceCountry}
                onChange={(e) => setSourceCountry(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none"
              >
                <option value="all">{t('allSources')}</option>
                {availableCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('targetMarket')}</label>
              <select
                value={targetCountry}
                onChange={(e) => setTargetCountry(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none"
              >
                {availableCountries.filter(c => c !== 'DE').map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('make')}</label>
              <select
                value={filterMake}
                onChange={(e) => { setFilterMake(e.target.value); setFilterModel('all'); setFilterVersion('all'); }}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none"
              >
                <option value="all">{t('allMakes')}</option>
                {availableMakes.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('model')}</label>
              <select
                value={filterModel}
                onChange={(e) => { setFilterModel(e.target.value); setFilterVersion('all'); }}
                disabled={filterMake === 'all'}
                className={`w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none ${filterMake === 'all' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="all">{filterMake === 'all' ? t('selectMakeFirst') : t('allModels')}</option>
                {filterMake !== 'all' && availableModels
                  .filter(model => allListings.some(car => car.make === filterMake && car.model === model))
                  .map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
              </select>
            </div>
          </div>
          {/* Row 2: Version/Trim, Fuel Type, Mileage, Year */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('versionTrim')}</label>
              <select
                value={filterVersion}
                onChange={(e) => setFilterVersion(e.target.value)}
                disabled={filterMake === 'all'}
                className={`w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none ${filterMake === 'all' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="all">{filterMake === 'all' ? t('selectMakeFirst') : t('allVersions')}</option>
                {filterMake !== 'all' && availableVersions
                  .filter(version => allListings.some(car => {
                    const makeMatch = car.make === filterMake;
                    const modelMatch = filterModel === 'all' || car.model === filterModel;
                    const fuelMatch = filterFuelType === 'all' || normalizeFuel(car.fuel_type) === filterFuelType;
                    return makeMatch && modelMatch && fuelMatch && normalizeVersion(car.version) === version;
                  }))
                  .map(version => (
                    <option key={version} value={version}>{version}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('fuelType')}</label>
              <select
                value={filterFuelType}
                onChange={(e) => { setFilterFuelType(e.target.value); setFilterVersion('all'); }}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none"
              >
                <option value="all">{t('allFuelTypes')}</option>
                {availableFuelTypes
                  .filter(fuelType => filterMake === 'all' || allListings.some(car => {
                    const makeMatch = car.make === filterMake;
                    const modelMatch = filterModel === 'all' || car.model === filterModel;
                    return makeMatch && modelMatch && normalizeFuel(car.fuel_type) === fuelType;
                  }))
                  .map(fuelType => (
                  <option key={fuelType} value={fuelType}>{fuelType.charAt(0).toUpperCase() + fuelType.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('mileage')}: {minMileage.toLocaleString()} - {maxMileage.toLocaleString()} km</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  max="300000"
                  step="10000"
                  value={minMileage}
                  onChange={(e) => setMinMileage(parseInt(e.target.value))}
                  className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-sm"
                  placeholder="Min"
                />
                <input
                  type="number"
                  min="0"
                  max="300000"
                  step="10000"
                  value={maxMileage}
                  onChange={(e) => setMaxMileage(parseInt(e.target.value))}
                  className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-sm"
                  placeholder="Max"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('year')}: {minYear} - {maxYear}</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="2000"
                  max="2026"
                  value={minYear}
                  onChange={(e) => setMinYear(parseInt(e.target.value))}
                  className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-sm"
                  placeholder="Min"
                />
                <input
                  type="number"
                  min="2000"
                  max="2026"
                  value={maxYear}
                  onChange={(e) => setMaxYear(parseInt(e.target.value))}
                  className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-sm"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>
          {/* Row 3: Max Price, Mileage Tolerance, Year Tolerance, AI Quality Score */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('maxPrice')}: €{maxPrice.toLocaleString()}</label>
              <input
                type="range"
                min="5000"
                max="200000"
                step="5000"
                value={maxPrice}
                onChange={(e) => setMaxPrice(parseInt(e.target.value))}
                style={sliderStyle(maxPrice, 5000, 200000)}
                className="w-full mt-1 accent-emerald-500 slider-mobile"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('mileageTolerance')}</label>
              <select
                value={mileageTolerance}
                onChange={(e) => setMileageTolerance(parseInt(e.target.value))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none"
              >
                <option value={5000}>{t('plusMinus5k')}</option>
                <option value={10000}>{t('plusMinus10k')}</option>
                <option value={20000}>{t('plusMinus20k')}</option>
                <option value={30000}>{t('plusMinus30k')}</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('yearTolerance')}</label>
              <select
                value={yearTolerance}
                onChange={(e) => setYearTolerance(parseInt(e.target.value))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none"
              >
                <option value={0}>{t('sameYear')}</option>
                <option value={1}>{t('plusMinus1Year')}</option>
                <option value={2}>{t('plusMinus2Years')}</option>
                <option value={3}>{t('plusMinus3Years')}</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('aiQualityScoreTitle')}</label>
              <div className="flex gap-1.5 mt-1">
                <button
                  onClick={() => toggleQuality('high')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                    qualityLevels.includes('high')
                      ? 'bg-emerald-500/30 text-emerald-400 ring-1 ring-emerald-500/50'
                      : 'bg-slate-900 text-slate-400 hover:text-slate-300'
                  }`}
                >
                  {t('qualityHigh')}
                </button>
                <button
                  onClick={() => toggleQuality('medium')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                    qualityLevels.includes('medium')
                      ? 'bg-amber-500/30 text-amber-400 ring-1 ring-amber-500/50'
                      : 'bg-slate-900 text-slate-400 hover:text-slate-300'
                  }`}
                >
                  {t('qualityMedium')}
                </button>
                <button
                  onClick={() => toggleQuality('low')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                    qualityLevels.includes('low')
                      ? 'bg-red-500/30 text-red-400 ring-1 ring-red-500/50'
                      : 'bg-slate-900 text-slate-400 hover:text-slate-300'
                  }`}
                >
                  {t('qualityLow')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterPanel;
